/**
 * "Gerenciar plano" da Global (spec 044): plano, etapa (Challenge ou Funded) com o dia, e a conta na corretora. A mesma
 * mecanica da Prop (spec 037): a tela mostra o que esta salvo, marca o que mudou e manda SO o que mudou; motivo e
 * origem sempre; mexeu na etapa ou na conta, confere antes de gravar. Quem decide se pode e o backend (conta invalida,
 * conta de outro plano vivo, etapa no futuro), e a recusa aparece como ele escreveu. Nada aqui escreve na corretora.
 *
 * Acende pelo modulo: o `GET .../gestao` so traz `modulos.corretora` onde o `nelogica-v2` esta ligado.
 */
import { Aviso, BlocoDeFormulario, Botao, Campo, CampoData, CampoMudado, CampoSelecao, CampoTextoLongo, Dialogo, Fatos, Segmentado, formatarDia, hojeEmSaoPaulo, soDigitos } from "@mtc/ui";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ORIGENS_DA_MUDANCA, STATUS_DA_MATRICULA, useAcao, useApi, type EtapaDaGlobal, type GestaoDaMatricula, type MudancaDaGestao } from "../api";

type Gestao = NonNullable<GestaoDaMatricula["modulos"]["corretora"]>;
type Formulario = { plano: string; etapa: EtapaDaGlobal; dia: string; conta: string; motivo: string; origem: string };
const plural = (n: number) => (n === 1 ? "1 mudança" : `${n} mudanças`);

export function FormularioDaGlobal({ g, c, matriculaId, aoFechar, aoSalvar }: { g: GestaoDaMatricula; c: Gestao; matriculaId: string; aoFechar: () => void; aoSalvar: (r: { mudancas: MudancaDaGestao[] }) => void }) {
  const { mudar } = useApi();
  const acao = useAcao();
  const salvo: Formulario = { plano: g.plano.slug, etapa: c.etapa.codigo, dia: c.etapa.desde, conta: c.conta ?? "", motivo: "", origem: "CONFERENCIA_INTERNA" };
  const [f, setF] = useState<Formulario>(salvo);
  const muda = (x: Partial<Formulario>) => setF((v) => ({ ...v, ...x }));
  const hoje = hojeEmSaoPaulo();
  const rotuloDa = (e: EtapaDaGlobal) => c.etapas.find((x) => x.codigo === e)?.rotulo ?? e;

  const mudouPlano = f.plano !== salvo.plano;
  const mudouEtapa = f.etapa !== salvo.etapa;
  const mudouDia = f.dia !== salvo.dia;
  const mudouConta = c.pode_mudar_conta && soDigitos(f.conta) !== soDigitos(salvo.conta);
  useEffect(() => {
    // Mudou a etapa: comeca hoje (na Funded a conta costuma ser nova, aberta agora). De volta na salva, o dia salvo.
    muda({ dia: f.etapa === salvo.etapa ? salvo.dia : hoje });
  }, [f.etapa]);

  const n = [mudouPlano, mudouEtapa, mudouDia, mudouConta].filter(Boolean).length;
  const falta =
    !f.dia ? "Informe desde quando o plano está na etapa."
    : f.dia > hoje ? "A etapa não pode começar no futuro."
    : mudouConta && !soDigitos(f.conta) ? "Informe a conta na corretora."
    : n > 0 && f.motivo.trim().length < 5 ? "Escreva o motivo."
    : null;
  const podeSalvar = n > 0 && !falta;
  const confere = mudouEtapa || mudouDia || mudouConta;

  const [conferindo, setConferindo] = useState(false);
  const erro = useRef<HTMLDivElement>(null);
  useEffect(() => { if (acao.erro && !conferindo) erro.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [acao.erro]);

  const enviar = async () => {
    let r: { mudancas: MudancaDaGestao[] } | undefined;
    const corpo = {
      motivo: f.motivo.trim(),
      origem: f.origem,
      ...(mudouPlano ? { plano_slug: f.plano } : {}),
      ...(mudouEtapa || mudouDia ? { etapa: f.etapa, dia_da_etapa: f.dia } : {}),
      ...(mudouConta ? { conta: soDigitos(f.conta) } : {}),
    };
    const gravou = await acao.rodar(async () => { r = await mudar<{ mudancas: MudancaDaGestao[] }>(`/v1/admin/matriculas/${matriculaId}/gestao`, corpo); });
    if (!gravou || !r) return;
    setConferindo(false);
    aoFechar();
    aoSalvar({ mudancas: r.mudancas });
  };
  const salvar = (ev: FormEvent) => {
    ev.preventDefault();
    if (!podeSalvar) return;
    // Mexeu na etapa ou na conta: confere antes de gravar, como na liberacao.
    if (confere) { acao.limpar(); setConferindo(true); return; }
    void enviar();
  };

  const planos = g.planos.some((p) => p.slug === g.plano.slug) ? g.planos : [g.plano, ...g.planos];
  const nomeDoPlano = (slug: string) => planos.find((p) => p.slug === slug)?.nome ?? slug;
  const comparado = (agora: string, era: string) => (agora === era ? "Continua igual" : `Antes: ${era}`);

  return (
    <>
      <form className="mtc-formulario" onSubmit={salvar} noValidate>
        {acao.erro && !conferindo && <div ref={erro}><Aviso tipo="erro">{acao.erro}</Aviso></div>}

        <BlocoDeFormulario titulo="Plano" descricao={<>Status: <strong>{STATUS_DA_MATRICULA[g.status].rotulo}</strong>. Suspender, retomar e encerrar continuam nos botões da ficha.</>}>
          <CampoMudado mudou={mudouPlano} antes={g.plano.nome}>
            <CampoSelecao rotulo="Plano" valor={f.plano} aoMudar={(v) => muda({ plano: v })} opcoes={planos.map((p) => ({ valor: p.slug, rotulo: p.nome }))} />
          </CampoMudado>
        </BlocoDeFormulario>

        <BlocoDeFormulario titulo="Etapa" descricao="Só o registro: mudar aqui não cobra taxa, não abre pedido e não muda nada na corretora.">
          <CampoMudado mudou={mudouEtapa} antes={rotuloDa(salvo.etapa)}>
            <Segmentado<EtapaDaGlobal> rotulo="Etapa" nome={`etapa-${matriculaId}`} valor={f.etapa} aoMudar={(v) => muda({ etapa: v })} opcoes={c.etapas.map((x) => ({ valor: x.codigo, rotulo: x.rotulo }))} />
          </CampoMudado>
          <CampoMudado mudou={mudouDia} antes={formatarDia(salvo.dia)}>
            <CampoData rotulo="Na etapa desde" valor={f.dia} aoMudar={(v) => muda({ dia: v })} max={hoje} dica={c.etapa.registrada || mudouEtapa ? "Hoje ou antes." : g.status === "AGUARDANDO_INICIO" ? "Sem registro, vale o dia da compra até a liberação. Hoje ou antes." : "Sem registro, vale o dia da ativação. Hoje ou antes."} />
          </CampoMudado>
        </BlocoDeFormulario>

        <BlocoDeFormulario titulo="Conta" descricao={c.pode_mudar_conta ? "Corrige o número guardado aqui. A conta na extranet da Nelogica não muda. Na Funded a conta costuma ser nova: troque etapa e conta juntas." : undefined}>
          <CampoMudado mudou={mudouConta} antes={salvo.conta || "sem conta"}>
            <Campo
              rotulo="Conta na corretora"
              valor={f.conta}
              aoMudar={(v) => muda({ conta: soDigitos(v).slice(0, 12) })}
              inputMode="numeric"
              mono
              autoComplete="off"
              disabled={!c.pode_mudar_conta}
              dica={c.pode_mudar_conta ? "Só números, como está na extranet." : "Este plano ainda espera a conta. Use “Liberar conta” na ficha: ele grava o número, ativa o plano e avisa o cliente."}
            />
          </CampoMudado>
        </BlocoDeFormulario>

        <BlocoDeFormulario titulo="Por que">
          <CampoTextoLongo rotulo="Motivo" valor={f.motivo} aoMudar={(v) => muda({ motivo: v })} maxLength={500} placeholder="Ex.: aprovado no Challenge, conta Funded criada na extranet." dica="Pelo menos 5 letras. Fica na trilha, com o seu nome; o cliente não lê." />
          <CampoSelecao rotulo="De onde veio o pedido" valor={f.origem} aoMudar={(v) => muda({ origem: v })} required opcoes={ORIGENS_DA_MUDANCA} />
        </BlocoDeFormulario>

        <div className="mtc-dialogo__rodape mtc-dialogo__rodape--fixo">
          {falta && n > 0 && <span className="mtc-texto-2" style={{ flex: "1 1 100%", fontSize: "var(--t-sm)" }} aria-live="polite">{falta}</span>}
          <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
          <Botao type="submit" carregando={acao.carregando && !conferindo} disabled={!podeSalvar}>{n ? `Salvar ${plural(n)}` : "Nada mudou ainda"}</Botao>
        </div>
      </form>

      <Dialogo
        aberto={conferindo}
        aoFechar={() => setConferindo(false)}
        titulo="Confere antes de salvar"
        descricao="O sistema não escreve na corretora. Confira na extranet que a conta é esta e está nesta etapa."
        rodape={<><Botao variante="fantasma" onClick={() => setConferindo(false)}>Voltar e corrigir</Botao><Botao carregando={acao.carregando} onClick={() => void enviar()}>Confirmar e salvar</Botao></>}
      >
        <div className="mtc-pilha" style={{ gap: "var(--e-3)" }}>
          {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
          <Fatos
            itens={[
              { rotulo: "Conta na corretora", valor: soDigitos(f.conta) || "Sem conta", mono: Boolean(soDigitos(f.conta)), detalhe: salvo.conta ? comparado(soDigitos(f.conta), salvo.conta) : "Nova" },
              { rotulo: "Etapa", valor: rotuloDa(f.etapa), detalhe: comparado(rotuloDa(f.etapa), rotuloDa(salvo.etapa)) },
              { rotulo: "Na etapa desde", valor: f.dia ? formatarDia(f.dia) : "Sem dia", detalhe: comparado(f.dia ? formatarDia(f.dia) : "", formatarDia(salvo.dia)) },
              ...(mudouPlano ? [{ rotulo: "Plano", valor: nomeDoPlano(f.plano), detalhe: `Antes: ${g.plano.nome}` }] : []),
            ]}
          />
        </div>
      </Dialogo>
    </>
  );
}
